import { useNotification } from 'admin';
import {
  AiModelEnum,
  CancellationReasonEnum,
  type ErrorType,
  getApiInspectionsV1CompaniesListQueryKey,
  type Inspection,
  InspectionResult,
  InspectionStatus,
  InspectionType,
  isApiFormError,
  isFieldErrorResponse,
  useApiInspectionsV1Create,
  useApiInspectionsV1Update,
  useApiTradeV1SiteMaterialsRetrieve,
  useQueryClient,
} from 'api';
import { TradeCompanySiteMaterialPicker } from 'features';
import { CheckboxField, DateTimeField, Form, SelectField, TextField, useForm, useWatch, yup, yupResolver } from 'form';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ActionRow, Alert, Button, Divider, Tooltip, Typography } from 'ui';

import { useAllInspectionCompanies } from '~/inspection-company';

import { getCompanyOptions } from '../../utils/getCompanyOptions/getCompanyOptions';
import { getInspectorOptionsByInspectionTime } from '../../utils/getInspectorOptions/getInspectorOptionsByInspectionTime';
import { isCompanyActive } from '../../utils/isCompanyActive/isCompanyActive';
import { matchInspectorToCompany } from '../../utils/matchInspectorToCompany/matchInspectorToCompany';
import { GradeSelector } from '../GradeSelector/GradeSelector';
import { InspectionLink } from '../InspectionLink/InspectionLink';
import { CANCELLATION_REASONS, INSPECTION_RESULTS, INSPECTION_STATUSES, INSPECTION_TYPE } from './options';

export type Props = {
  inspection?: Inspection;
  dealId?: number;
  variant: 'create' | 'update';
};

export const InspectionMutate = ({ inspection, dealId, variant }: Props) => {
  const navigate = useNavigate();
  const { data: companyData } = useAllInspectionCompanies();

  const [selectedInspectionTime, setSelectedInspectionTime] = useState<Date>(
    inspection?.time ? new Date(inspection.time) : new Date()
  );

  const companyOptions = useMemo(() => {
    if (!companyData) {
      return [];
    }
    return getCompanyOptions(companyData);
  }, [companyData]);

  const inspectorOptions = useMemo(() => {
    if (!companyData) {
      return [];
    }
    return getInspectorOptionsByInspectionTime(companyData, selectedInspectionTime);
  }, [companyData, selectedInspectionTime]);

  const schema = useMemo(() => {
    return yup.object().shape(
      {
        cancellation_reason: yup
          .string()
          .oneOf(Object.values(CancellationReasonEnum))
          .nullable()
          .when('status', {
            is: (status: string) => {
              return status === 'CA';
            },
            otherwise: () => yup.string().nullable().notRequired(),
            then: () => yup.string().required('Cancellation reason is required for cancelled inspections'),
          }),
        deal: yup.number().nullable().notRequired(),
        external_report_link: yup.string().url('Link must be a valid URL').nullable().notRequired(),
        grade: yup.number().nullable().notRequired(),
        inspected_correctly: yup.string().oneOf(['yes', 'no']).notRequired(),
        inspection_company: yup
          .number()
          .nullable()
          .notRequired()
          .test({
            message: 'Inspection company must be active',
            name: 'company-active',
            test: isCompanyActive(companyData),
          }),
        inspection_result: yup.string().oneOf(Object.values(InspectionResult)).required(),
        inspector: yup
          .number()
          .nullable()
          .notRequired()
          .test(
            'inspector-company',
            'Inspector must belong to the selected company',
            matchInspectorToCompany(inspectorOptions, companyOptions)
          ),
        invalid_inspection_reason: yup.string().optional(),
        is_ai_gravi_sample_enabled: yup.boolean(),
        model: yup.string().nullable().notRequired(),
        notes: yup.string().nullable().notRequired(),
        site_material: yup
          .number()
          .required('Site material is required')
          .transform((value) => (value === Number.POSITIVE_INFINITY ? null : value)),
        status: yup.string().oneOf(Object.values(InspectionStatus)).required(),
        time: yup.string().required(),
        trade_company: yup.number().notRequired().optional(),
        type: yup.string().oneOf(Object.values(InspectionType)).required(),
      },
      [['inspection_company', 'inspector']]
    );
  }, [companyData, companyOptions, inspectorOptions]);

  type FormValues = yup.InferType<typeof schema>;
  const { open } = useNotification();

  const form = useForm<FormValues>({
    defaultValues: {
      status: 'BL',
      ...inspection,
      cancellation_reason: inspection?.cancellation_reason || null,
      deal: dealId || null,
      inspection_result: inspection?.inspection_result || InspectionResult.PENDING,
      invalid_inspection_reason: inspection?.invalid_inspection_reason || '',
      is_ai_gravi_sample_enabled: Boolean(inspection?.is_ai_gravi_sample_enabled),
      site_material: inspection?.site_material ?? undefined,
      time: inspection?.time ?? undefined,
    },
    resolver: yupResolver(schema),
  });

  const { setValue, control } = form;
  const queryClient = useQueryClient();

  const {
    inspected_correctly,
    status,
    site_material: selectedSiteMaterial,
    grade,
    is_ai_gravi_sample_enabled,
  } = useWatch<FormValues>({ control });
  const { data: siteMaterialData } = useApiTradeV1SiteMaterialsRetrieve(String(selectedSiteMaterial), {
    query: {
      enabled: Boolean(selectedSiteMaterial),
    },
  });

  useEffect(() => {
    setValue('deal', dealId || null);
  }, [dealId, setValue]);

  useEffect(() => {
    if (siteMaterialData) {
      setValue('trade_company', siteMaterialData?.site?.trade_company.id);
    }
  }, [setValue, siteMaterialData]);

  const handleTimeChange = (value: string | null) => {
    if (!value) {
      return;
    }
    const date = new Date(value);
    setSelectedInspectionTime(date);
  };

  const handleSuccess = () => {
    open?.({
      message: 'Changes saved',
      type: 'success',
    });
    queryClient.invalidateQueries({
      queryKey: getApiInspectionsV1CompaniesListQueryKey(),
    });
  };

  const handleError = (error: ErrorType<unknown>) => {
    if (isApiFormError(error)) {
      open?.({
        description: error?.response?.data.detail,
        message: 'Error signing in',
        type: 'error',
      });
      return;
    }
    if (isFieldErrorResponse(error)) {
      Object.keys(error.response?.data).forEach((field) => {
        form.setError(field as keyof FormValues, { message: error.response.data[field] });
      });
      if (error.response?.data.non_field_errors) {
        open?.({
          description: error?.response?.data.non_field_errors[0],
          message: 'Error mutating inspection',
          type: 'error',
        });
      }
      return;
    }

    open?.({
      message: 'Error mutating inspection',
      type: 'error',
    });
  };

  const { mutate: create, isPending: isCreatePending } = useApiInspectionsV1Create({
    mutation: {
      onError: handleError,
      onSuccess: (data) => {
        handleSuccess();
        navigate(`/ground-control/inspections/${data.id}`);
      },
    },
  });

  const { mutate: edit, isPending: isEditPending } = useApiInspectionsV1Update({
    mutation: {
      onError: handleError,
      onSuccess: handleSuccess,
    },
  });

  const handleSubmit = (values: FormValues) => {
    if (inspection) {
      edit({
        data: values,
        id: inspection.id,
      });
      return;
    }
    create({
      data: values,
    });
  };

  const aiSampleEnabled = useMemo(() => {
    if (!selectedSiteMaterial || !siteMaterialData) {
      return false;
    }
    return (
      siteMaterialData.material_spec?.ai_model === AiModelEnum.alu ||
      siteMaterialData.material_spec?.ai_model === AiModelEnum.briquette
    );
  }, [selectedSiteMaterial, siteMaterialData]);

  const aiSampleCheckboxText = useMemo(() => {
    if (!selectedSiteMaterial) {
      return 'A site material is required to enable samples';
    }
    if (!aiSampleEnabled) {
      return 'AI Gravi Sample is not available for this material';
    }
    return '';
  }, [selectedSiteMaterial, aiSampleEnabled]);

  useEffect(() => {
    if (!aiSampleEnabled) {
      setValue('is_ai_gravi_sample_enabled', false);
    } else {
      setValue('is_ai_gravi_sample_enabled', Boolean(inspection?.is_ai_gravi_sample_enabled));
    }
  }, [aiSampleEnabled, inspection?.is_ai_gravi_sample_enabled, setValue]);

  return (
    <Form {...form} onSubmit={handleSubmit}>
      <Typography variant="h6">
        {inspection ? 'Edit' : 'Create'} inspection{inspection ? ` ${inspection.slug}` : null}
      </Typography>
      {form.formState.errors.deal ? <Alert severity="error">{String(form.formState.errors.deal.message)}</Alert> : null}
      <TradeCompanySiteMaterialPicker
        slotProps={{
          siteMaterial: {
            isOptionEqualToValue: (option, value) => option?.value === String(value),
            label: 'Site Material',
            name: 'site_material',
          },
          tradeCompany: {
            defaultValue: siteMaterialData?.site?.trade_company.id,
            name: 'trade_company',
          },
        }}
      />
      <Divider />
      <DateTimeField
        required
        format="DD/MM/YYYY HH:mm"
        ampm={false}
        label="Inspection time"
        name="time"
        onChange={handleTimeChange}
      />
      <SelectField required name="status" id="inspection-status" label="Status" options={INSPECTION_STATUSES} />
      <SelectField
        name="cancellation_reason"
        label="Cancellation reason (if applicable)"
        disabled={status !== 'CA'}
        options={CANCELLATION_REASONS}
      />
      <SelectField
        required
        name="type"
        label="Type"
        options={INSPECTION_TYPE}
        data-testid="inspection-type"
        inputProps={{
          'data-testid': 'inspection-type-input',
        }}
      />
      <SelectField
        required={false}
        name="inspection_company"
        data-testid="inspection-company"
        label="Company"
        disabled={companyOptions.length === 0}
        options={companyOptions}
        inputProps={{
          'data-testid': 'inspection-company-input',
        }}
      />
      <SelectField
        required={false}
        name="inspector"
        label="Inspector"
        disabled={inspectorOptions.length === 0}
        options={inspectorOptions}
      />
      <GradeSelector />
      <TextField multiline name="notes" label="Notes" />
      <TextField name="external_report_link" label="Link to external report" />
      <Tooltip title={aiSampleCheckboxText}>
        <div>
          <CheckboxField name="is_ai_gravi_sample_enabled" label="Enable AI Gravi Sample" disabled={!aiSampleEnabled} />
        </div>
      </Tooltip>
      {variant === 'update' && (
        <>
          <Divider />
          <SelectField name="inspection_result" label="Inspection Result" options={INSPECTION_RESULTS} />
          <SelectField
            name="inspected_correctly"
            label="Inspected correctly"
            options={[
              {
                label: 'Not specified',
                value: undefined,
              },
              { label: 'Yes', value: 'yes' },
              { label: 'No', value: 'no' },
            ]}
          />
          {inspected_correctly === 'no' ? (
            <TextField
              multiline
              name="invalid_inspection_reason"
              label="Invalid Inspection Reason (if applicable)"
              minRows={2}
            />
          ) : null}
        </>
      )}
      <ActionRow>
        <InspectionLink inspectionId={inspection?.id} disabled={!grade && !is_ai_gravi_sample_enabled} />
        <Button fullWidth type="submit" variant="primary" loading={isCreatePending || isEditPending}>
          Save
        </Button>
      </ActionRow>
    </Form>
  );
};
